import { useEffect, useRef, useMemo, useCallback } from "react";
import { trackDiagnostic } from "../utils/diagnostics";
import { useVehiclesSocketStore } from "../store/admin/vehiclesSocketStore";
import { useVehicleFootprintTrackingStore } from "../store/admin/vehicleFootprintTrackingStore";
import { useCompaniesStore } from "../store/admin/companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "./useIsReseller";
import { useGroupsStore } from "../store/admin/groupsStore";
import { useUserStore } from "../store/userStore";
import { connectSocket, getSocket } from "../socket";
import { queryClient } from "../lib/queryClient";
import { allDevicesQueryOptions } from "../features/devices/queries";
import {
  classifyDevicesError,
  DEVICES_LOAD_ERRORS,
  DevicesLoadError,
  type DevicesLoadErrorCode,
} from "../features/devices/devicesDiagnostics";
import { driverDisplayName } from "../utils/driverName";

interface DeviceFromAPI {
  id: string;
  fullId?: string;
  identifier?: string;
  model?: string;
  status_id?: string;
  type?: string;
  company_id?: string;
  companyId?: string;
  vehicleId?: string;
  vehicle_id?: string;
  remolque_id?: string;
  serial?: string;
  sim_card?: string;
  brand_id?: string;
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
  vehicle?: {
    id?: string;
    brand?: string;
    model?: string;
    year?: number;
    plates?: string;
    plate?: string;
    color?: string;
    name?: string;
    type?: string;
  };
  driver?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    license_id?: string;
    photo?: string;
    experience?: string;
    phone?: string;
    email?: string;
  };
  group?: string;
  icon?: string;
  imei?: string;
  lastPosition?: {
    lat?: number;
    lon?: number;
    speed?: number | null;
    heading?: number | null;
    timeStamp?: number;
    altitude?: number | null;
    accuracy?: number | null;
    battery?: number | null;
    ignition?: boolean;
    event_code?: number;
    params?: {
      engine_status?: number;
      event_code?: number;
      odo_km?: number;
    };
  };
}

const DEVICE_RETRY_DELAYS_MS = [2000, 5000];

const resolveCompanyId = (companyId?: string) =>
  companyId ||
  useVehiclesSocketStore.getState().selectedCompany ||
  useCompaniesStore.getState().companyId ||
  useCompaniesStore.getState().companyUser ||
  "";

const normalizeDevices = (data: DeviceFromAPI[], resolvedCompanyId: string) =>
  data.map((device) => ({
    id: device.id,
    fullId: device.fullId || device.identifier,
    identifier: device.identifier,
    companyId: device.companyId || device.company_id || resolvedCompanyId || "",
    vehicle_id: device.vehicle_id || device.vehicleId,
    remolque_id: device.remolque_id,
    brand_id: device.brand_id,
    
    model: device.model,
    status_id: device.status_id,
    type: device.type,
    serial: device.serial,
    sim_card: device.sim_card,
    imei: device.imei || device.identifier,

    vehicle: device.vehicle ? {
      ...device.vehicle,
      plates: device.vehicle.plates || device.vehicle.plate,
    } : undefined,

    driver: device.driver ? {
      id: device.driver.id,
      name: driverDisplayName(device.driver) || "Sin asignar",
      licenseId: device.driver.license_id || "",
      photo: device.driver.photo || "",
      experience: device.driver.experience || "",
      phone: device.driver.phone || "",
      email: device.driver.email,
    } : undefined,

    group: device.group,
    icon: device.icon,

    lastPosition: device.lastPosition,
  }));

type NormalizedDevice = ReturnType<typeof normalizeDevices>[number];

export const loadDevicesFromAPI = async (
  companyId?: string,
  forceRefresh = false,
): Promise<NormalizedDevice[]> => {
  const resolvedCompanyId = resolveCompanyId(companyId);
  const options = allDevicesQueryOptions(resolvedCompanyId || null);
  const raw: unknown = await queryClient.fetchQuery(
    forceRefresh ? { ...options, staleTime: 0 } : options,
  );
  if (!Array.isArray(raw)) {
    throw new DevicesLoadError("API_BAD_SHAPE", { data_type: typeof raw });
  }
  return normalizeDevices(raw, resolvedCompanyId);
};

export const fetchDevicesFromAPI = (companyId?: string, forceRefresh = false) =>
  loadDevicesFromAPI(companyId, forceRefresh).catch((error: unknown) => {
    console.error("Error fetching devices:", error);
    return null;
  });

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const loadDevicesWithRetry = async (
  companyId: string | undefined,
  forceRefresh: boolean,
  attempt = 0,
): Promise<NormalizedDevice[]> => {
  try {
    return await loadDevicesFromAPI(companyId, forceRefresh || attempt > 0);
  } catch (error) {
    const failure = classifyDevicesError(error);
    if (!failure) throw error;
    const delay = DEVICE_RETRY_DELAYS_MS[attempt];
    if (delay === undefined || !DEVICES_LOAD_ERRORS[failure.code].autoRetry) {
      throw new DevicesLoadError(failure.code, {
        ...failure.details,
        attempts: attempt + 1,
      });
    }
    await wait(delay);
    return loadDevicesWithRetry(companyId, true, attempt + 1);
  }
};

export const useVehiclesSocketSubscription = () => {
  const vehicles = useVehiclesSocketStore((state) => state.vehicles);
  const loading = useVehiclesSocketStore((state) => state.loading);
  const isConnected = useVehiclesSocketStore((state) => state.isConnected);
  const allCompanyDeviceIds = useVehiclesSocketStore((state) => state.allCompanyDeviceIds);
  const initializeWithDevices = useVehiclesSocketStore((state) => state.initializeWithDevices);
  const connect = useVehiclesSocketStore((state) => state.connect);
  const disconnect = useVehiclesSocketStore((state) => state.disconnect);
  const resubscribe = useVehiclesSocketStore((state) => state.resubscribe);
  const setAwaitingCompanySelection = useVehiclesSocketStore((state) => state.setAwaitingCompanySelection);
  const setLoading = useVehiclesSocketStore((state) => state.setLoading);
  const setLoadError = useVehiclesSocketStore((state) => state.setLoadError);
  const reloadRequestId = useVehiclesSocketStore((state) => state.reloadRequestId);
  const resetStore = useVehiclesSocketStore((state) => state.resetStore);

  const selectedGroup = useVehiclesSocketStore((state) => state.selectedGroup);
  const selectedCompany = useVehiclesSocketStore((state) => state.selectedCompany);

  const companyId = useCompaniesStore((state) => state.companyId);

  const groups = useGroupsStore((state) => state.groups);

  // Footprint tracking
  const activeFootprintIds = useVehicleFootprintTrackingStore((state) =>
    Object.keys(state.activeFootprints).sort().join(',')
  );
  const addFootprintPoint = useVehicleFootprintTrackingStore((state) => state.addFootprintPoint);

  const vehiclesRef = useRef(vehicles);
  vehiclesRef.current = vehicles;

  const hasHandledInit = useRef(false);
  const forceRefreshRef = useRef(false);
  const lastFailureRef = useRef<DevicesLoadErrorCode | null>(null);
  const firstFailureAtRef = useRef<number | null>(null);
  const handledReloadRef = useRef(reloadRequestId);
  const previousGroupRef = useRef<string>(
    useVehiclesSocketStore.getState().selectedGroup
  );
  const previousCompanyRef = useRef<string | null>(null);

  const user = useUserStore((state) => state.user);
  const userRoleId = user?.role_id || localStorage.getItem("userRoleId") || "";
  const isReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;

  const initializeAndConnect = useCallback(async () => {
    if (hasHandledInit.current) return;

    if (isReseller && !selectedCompany) {
      const storeState = useVehiclesSocketStore.getState();
      if (!storeState.isInitialized) {
        setAwaitingCompanySelection(true);
      }
      return;
    }

    const targetCompanyId = isReseller ? selectedCompany : companyId;

    if (!isReseller && !companyId) {
      return;
    }

    // Check if the store already has data for this company (reconnect scenario)
    const storeState = useVehiclesSocketStore.getState();
    if (storeState.isInitialized && storeState.lastInitializedCompanyId === targetCompanyId) {
      console.log("[Socket] Reconnecting to existing session (same company)");
      hasHandledInit.current = true;
      previousCompanyRef.current = targetCompanyId;

      const deviceIds = storeState.allCompanyDeviceIds;
      if (deviceIds.length > 0) {
        connect(deviceIds);
      }
      return;
    }

    // Different company than what's in the store - reset first
    if (storeState.isInitialized && storeState.lastInitializedCompanyId !== targetCompanyId) {
      console.log("[Socket] Company changed, resetting store");
      disconnect();
      resetStore();
    }

    // Full initialization
    setLoading(true);
    setLoadError(null);
    const forceRefresh = forceRefreshRef.current;
    forceRefreshRef.current = false;

    // Pre-connect socket while API is loading (saves ~500ms-1s)
    connectSocket();

    try {
      const devices = await loadDevicesWithRetry(
        targetCompanyId || undefined,
        forceRefresh,
      );

      const currentTarget = isReseller
        ? useVehiclesSocketStore.getState().selectedCompany
        : useCompaniesStore.getState().companyId;
      if (currentTarget !== targetCompanyId) return;

      // Prevent double initialization (React Strict Mode fires effects twice,
      // causing two concurrent API calls - only the first to resolve should initialize)
      if (hasHandledInit.current) return;
      const currentState = useVehiclesSocketStore.getState();
      if (currentState.isInitialized && currentState.lastInitializedCompanyId === targetCompanyId) {
        hasHandledInit.current = true;
        previousCompanyRef.current = targetCompanyId;
        if (!currentState.isConnected && currentState.allCompanyDeviceIds.length > 0) {
          connect(currentState.allCompanyDeviceIds);
        }
        return;
      }

      initializeWithDevices(devices);
      useVehiclesSocketStore.setState({ lastInitializedCompanyId: targetCompanyId });

      if (lastFailureRef.current) {
        trackDiagnostic("devices_load_recovered", {
          previous_code: lastFailureRef.current,
          device_count: devices.length,
          blocked_ms: firstFailureAtRef.current
            ? Date.now() - firstFailureAtRef.current
            : undefined,
        });
        lastFailureRef.current = null;
        firstFailureAtRef.current = null;
      }

      if (devices.length === 0) {
        trackDiagnostic("devices_empty", {
          company_id_sent: targetCompanyId,
          is_reseller: isReseller,
        });
        hasHandledInit.current = true;
        previousCompanyRef.current = targetCompanyId;
        return;
      }

      const deviceIds = devices.map((d: DeviceFromAPI) => d.id);

      connect(deviceIds);

      hasHandledInit.current = true;
      previousCompanyRef.current = targetCompanyId;
    } catch (err: unknown) {
      console.error("[Socket] Error initializing:", err);
      setLoading(false);
      const failure = classifyDevicesError(err);
      if (!failure) return;
      lastFailureRef.current = failure.code;
      firstFailureAtRef.current = firstFailureAtRef.current ?? Date.now();
      const storeAtFailure = useVehiclesSocketStore.getState();
      trackDiagnostic("devices_load_failed", {
        code: failure.code,
        company_id_sent: targetCompanyId,
        is_reseller: isReseller,
        socket_connected: storeAtFailure.isConnected,
        had_devices: storeAtFailure.vehicles.length > 0,
        ...failure.details,
      });
      setLoadError(failure.code);
    }
  }, [companyId, selectedCompany, isReseller, initializeWithDevices, connect, setAwaitingCompanySelection, setLoading, setLoadError, disconnect, resetStore]);

  useEffect(() => {
    if (reloadRequestId === handledReloadRef.current) return;
    handledReloadRef.current = reloadRequestId;
    disconnect();
    resetStore();
    hasHandledInit.current = false;
    forceRefreshRef.current = true;
    initializeAndConnect();
  }, [reloadRequestId, disconnect, resetStore, initializeAndConnect]);

  useEffect(() => {
    const resume = () => {
      if (document.visibilityState !== "visible") return;
      const state = useVehiclesSocketStore.getState();
      if (state.loadError && DEVICES_LOAD_ERRORS[state.loadError].autoRetry) {
        state.requestReload();
        return;
      }
      if (
        state.isInitialized &&
        !state.isConnected &&
        state.currentSubscribedDeviceIds.length > 0 &&
        !getSocket().active
      ) {
        connectSocket();
      }
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("online", resume);
    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("online", resume);
    };
  }, []);

  // Sync selectedCompany from companiesStore for resellers
  // When entering the app directly (page refresh/direct URL), vehiclesSocketStore.selectedCompany
  // is null but companiesStore.companyId is already initialized from localStorage.
  // This also handles company changes in DumaxAdminLayout which go through companiesStore.setCompanyId.
  useEffect(() => {
    if (!isReseller) return;
    if (companyId && companyId !== selectedCompany) {
      useVehiclesSocketStore.getState().setSelectedCompany(companyId);
    }
  }, [isReseller, companyId, selectedCompany]);

  // Main mount/unmount lifecycle
  useEffect(() => {
    initializeAndConnect();

    return () => {
      if (hasHandledInit.current) {
        // Only disconnect socket, preserve store data for fast reconnect
        disconnect();
        hasHandledInit.current = false;
      }
    };
  }, [initializeAndConnect, disconnect]);

  // Handle reseller company change
  useEffect(() => {
    if (!isReseller) return;

    if (hasHandledInit.current && selectedCompany !== previousCompanyRef.current) {
      disconnect();
      resetStore();
      hasHandledInit.current = false;
      previousGroupRef.current = "all";
      previousCompanyRef.current = selectedCompany;

      const timeoutId = setTimeout(() => {
        initializeAndConnect();
      }, 50);

      return () => clearTimeout(timeoutId);
    } else if (!hasHandledInit.current && selectedCompany) {
      // First company selection or returning from admin with new company
      setAwaitingCompanySelection(false);
      setLoading(true);

      const timeoutId = setTimeout(() => {
        initializeAndConnect();
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedCompany, isReseller, disconnect, initializeAndConnect, resetStore, setLoading, setAwaitingCompanySelection]);

  // Handle group change
  useEffect(() => {
    if (!hasHandledInit.current) return;
    if (selectedGroup === previousGroupRef.current) return;

    previousGroupRef.current = selectedGroup;

    let deviceIdsToSubscribe: string[];

    if (selectedGroup === "all") {
      deviceIdsToSubscribe = allCompanyDeviceIds;
    } else {
      const selectedGroupData = groups.find((g) => g.id === selectedGroup);

      if (selectedGroupData && selectedGroupData.devices_ids && selectedGroupData.devices_ids.length > 0) {
        deviceIdsToSubscribe = selectedGroupData.devices_ids;
      } else {
        deviceIdsToSubscribe = allCompanyDeviceIds;
      }
    }

    if (deviceIdsToSubscribe.length > 0) {
      resubscribe(deviceIdsToSubscribe);
    }
  }, [selectedGroup, groups, allCompanyDeviceIds, resubscribe]);

  // Footprint tracking
  const footprintIdsArray = useMemo(() =>
    activeFootprintIds ? activeFootprintIds.split(',').filter(Boolean) : [],
    [activeFootprintIds]
  );

  const trackedFootprintPositions = useVehiclesSocketStore((state) => {
    if (footprintIdsArray.length === 0) return '';
    return footprintIdsArray.map(id => {
      const idx = state.vehiclesMap.get(id);
      if (idx === undefined) return '';
      const v = state.vehicles[idx];
      return `${id}:${v.latitude},${v.longitude}`;
    }).join('|');
  });

  useEffect(() => {
    if (footprintIdsArray.length === 0 || !trackedFootprintPositions) return;

    footprintIdsArray.forEach((vehicleId) => {
      const vehicle = vehiclesRef.current.find((v) => v.id === vehicleId);
      if (vehicle && vehicle.latitude && vehicle.longitude) {
        addFootprintPoint(vehicleId, vehicle.latitude, vehicle.longitude);
      }
    });
  }, [trackedFootprintPositions, footprintIdsArray, addFootprintPoint]);

  return {
    vehicles,
    loading,
    isConnected,
  };
};

export default useVehiclesSocketSubscription;